import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { virtualCompetitionAPI } from '../api/endpoints';
import {
  FaTrophy, FaUsers, FaCoins, FaClock, FaFire,
  FaSync, FaHistory, FaUserCheck, FaCheckCircle,
  FaHourglassHalf, FaExclamationTriangle,
} from 'react-icons/fa';
import toast from 'react-hot-toast';
import LoadingSpinner from '../components/common/LoadingSpinner';
import './VirtualCompetitions.css';

const TABS = [
  { key: 'all',      label: 'All Competitions', icon: FaTrophy },
  { key: 'mine',     label: 'My Competitions',  icon: FaUserCheck },
];

const VirtualCompetitions = () => {
  const { isAuthenticated } = useAuth();

  const [activeTab, setActiveTab] = useState('all');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [allComps, setAllComps] = useState([]);
  const [myComps, setMyComps] = useState([]);
  const [myLoading, setMyLoading] = useState(false);
  const [myError, setMyError] = useState(null);

  // ==================== FETCH ALL ====================
  const fetchAll = useCallback(async (showLoader = false) => {
    if (showLoader) setLoading(true);
    try {
      const res = await virtualCompetitionAPI.list();
      let data = res?.data;

      // Strip WP DB error HTML prefix if present
      if (typeof data === 'string') {
        const start = data.indexOf('{');
        if (start !== -1) data = JSON.parse(data.slice(start));
      }

      const items = Array.isArray(data?.data) ? data.data
                  : Array.isArray(data) ? data
                  : [];

      setAllComps(items);
    } catch (err) {
      console.error('[VirtualCompetitions] fetch all failed:', err);
      if (showLoader) toast.error('Failed to load competitions');
    } finally {
      if (showLoader) setLoading(false);
    }
  }, []);

  // ==================== FETCH MY ====================
  const fetchMine = useCallback(async (showLoader = false) => {
    if (!isAuthenticated) {
      setMyError('Please log in to see your competitions.');
      setMyComps([]);
      return;
    }
    if (showLoader) setMyLoading(true);
    setMyError(null);
    try {
      const res = await virtualCompetitionAPI.listJoined();
      let data = res?.data;

      if (typeof data === 'string') {
        const start = data.indexOf('{');
        if (start !== -1) data = JSON.parse(data.slice(start));
      }

      const items = Array.isArray(data?.data) ? data.data
                  : Array.isArray(data) ? data
                  : [];

      setMyComps(items);
    } catch (err) {
      console.error('[VirtualCompetitions] fetch mine failed:', err);
      const status = err?.response?.status;
      setMyError(
        status === 401 ? 'Session expired — please log in again.' :
        status === 404 ? 'Endpoint not found on the server.' :
        err?.response?.data?.message || 'Failed to load your competitions.'
      );
      setMyComps([]);
    } finally {
      if (showLoader) setMyLoading(false);
    }
  }, [isAuthenticated]);

  // Initial load + tab switch
  useEffect(() => {
    fetchAll(true);
  }, [fetchAll]);

  useEffect(() => {
    if (activeTab === 'mine') fetchMine(true);
  }, [activeTab, fetchMine]);

  // Poll every 20s while any tab has live competitions
  useEffect(() => {
    const hasLiveAll  = allComps.some(c => c.status === 'live');
    const hasLiveMine = myComps.some(c => c.status === 'live');
    const shouldPoll  = activeTab === 'all' ? hasLiveAll : hasLiveMine;
    if (!shouldPoll) return;

    const t = setInterval(() => {
      if (document.visibilityState === 'visible') {
        activeTab === 'all' ? fetchAll(false) : fetchMine(false);
      }
    }, 20000);
    return () => clearInterval(t);
  }, [activeTab, allComps, myComps, fetchAll, fetchMine]);

  const handleRefresh = async () => {
    setRefreshing(true);
    if (activeTab === 'all') await fetchAll(false);
    else await fetchMine(false);
    setRefreshing(false);
  };

  // ==================== RENDER ====================
  const activeList = activeTab === 'all' ? allComps : myComps;
  const activeLoading = activeTab === 'all' ? loading : myLoading;

  return (
    <div className="vc-page">
      {/* Hero */}
      <div className="vc-hero">
        <div>
          <h1><FaTrophy className="hero-icon" /> Virtual Competitions</h1>
          <p>Rolling prediction contests. 5 games every 7 minutes. Win the prize pool.</p>
        </div>
        <button
          className="vc-refresh-btn"
          onClick={handleRefresh}
          disabled={refreshing}
          title="Refresh"
          type="button"
        >
          {refreshing ? <LoadingSpinner size="small" /> : <FaSync />}
        </button>
      </div>

      {/* Tabs */}
      <div className="vc-tabs">
        {TABS.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            className={`vc-tab ${activeTab === key ? 'active' : ''}`}
            onClick={() => setActiveTab(key)}
            type="button"
          >
            <Icon />
            <span>{label}</span>
            {key === 'mine' && myComps.length > 0 && (
              <span className="vc-tab-count">{myComps.length}</span>
            )}
          </button>
        ))}
      </div>

      {/* Content */}
      {activeLoading ? (
        <div className="vc-loading"><LoadingSpinner size="large" /></div>
      ) : activeTab === 'mine' && myError ? (
        <div className="vc-empty">
          <FaExclamationTriangle className="vc-empty-icon" />
          <h3>Couldn't load your competitions</h3>
          <p>{myError}</p>
          <button className="btn btn-primary" onClick={() => fetchMine(true)} type="button">
            Try Again
          </button>
        </div>
      ) : activeList.length === 0 ? (
        <div className="vc-empty">
          <FaTrophy className="vc-empty-icon" />
          <h3>
            {activeTab === 'all'
              ? 'No competitions right now'
              : "You haven't joined any competitions yet"}
          </h3>
          <p>
            {activeTab === 'all'
              ? 'Check back soon — creators drop new contests regularly.'
              : 'Browse the All Competitions tab and join one to get started.'}
          </p>
          {activeTab === 'mine' && (
            <button className="btn btn-primary" onClick={() => setActiveTab('all')} type="button">
              Browse Competitions
            </button>
          )}
        </div>
      ) : (
        <div className="vc-grid">
          {activeList.map((comp) => (
            <VirtualCompetitionCard key={comp.id} comp={comp} />
          ))}
        </div>
      )}
    </div>
  );
};

// ==================== CARD ====================
const VirtualCompetitionCard = ({ comp }) => {
  const statusMeta = {
    draft:     { label: 'Draft',     cls: 'draft',     icon: null },
    open:      { label: 'Open',      cls: 'open',      icon: <FaFire /> },
    live:      { label: 'Live',      cls: 'live',      icon: <FaFire /> },
    completed: { label: 'Completed', cls: 'completed', icon: <FaCheckCircle /> },
    cancelled: { label: 'Cancelled', cls: 'cancelled', icon: null },
  };
  const status = statusMeta[comp.status] || statusMeta.open;

  const entryFee = Number(comp.entry_fee) || 0;
  const prizePool = Number(comp.prize_pool) || 0;
  const gamesPlayed = Number(comp.games_played) || 0;
  const totalGames = Number(comp.total_games) || 0;
  const progress = totalGames > 0 ? Math.min(100, (gamesPlayed / totalGames) * 100) : 0;

  const my = comp.my_participation;
  const hasJoined = !!my;

  // Countdown (live only)
  let closesInMin = null;
  let closesInSec = null;
  if (comp.status === 'live' && comp.round_closes_at) {
    const ms = new Date(comp.round_closes_at + 'Z').getTime() - Date.now();
    if (ms > 0) {
      closesInMin = Math.floor(ms / 60000);
      closesInSec = Math.floor((ms % 60000) / 1000);
    }
  }

  return (
    <Link
      to={`/virtual-competitions/play/${comp.share_slug}`}
      className={`vc-card status-${status.cls}`}
    >
      {/* Top: status + live timer */}
      <div className="vc-card-top">
        <span className={`vc-status-badge ${status.cls}`}>
          {status.icon} {status.label}
        </span>
        {comp.status === 'live' && closesInMin !== null && (
          <span className="vc-round-timer">
            <FaClock /> R{comp.current_round} · {closesInMin}:{String(closesInSec).padStart(2, '0')}
          </span>
        )}
      </div>

      {/* Title */}
      <h3 className="vc-card-title">{comp.name}</h3>
      {comp.description && (
        <p className="vc-card-desc">
          {comp.description.length > 90
            ? `${comp.description.slice(0, 90)}…`
            : comp.description}
        </p>
      )}

      {/* Creator */}
      <div className="vc-card-creator">
        {comp.creator_avatar && <img src={comp.creator_avatar} alt="" />}
        <span>Hosted by <strong>{comp.creator_name}</strong></span>
      </div>

      {/* Stats */}
      <div className="vc-card-stats">
        <span className="vc-stat">
          <FaUsers /> {comp.player_count} player{comp.player_count !== 1 ? 's' : ''}
        </span>
        <span className="vc-stat gold">
          <FaCoins /> {prizePool.toFixed(2)} pool
        </span>
        <span className="vc-stat">
          Entry: {entryFee > 0 ? `${entryFee.toFixed(2)} $XPUNT` : 'Free'}
        </span>
        <span className="vc-stat">
          <FaTrophy /> {comp.total_winners} winner{comp.total_winners !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Progress */}
      {totalGames > 0 && (
        <div className="vc-progress">
          <div className="vc-progress-bar">
            <div className="vc-progress-fill" style={{ width: `${progress}%` }} />
          </div>
          <span className="vc-progress-label">
            {gamesPlayed} / {totalGames} games
          </span>
        </div>
      )}

      {/* User status */}
      {hasJoined && (
        <div className="vc-user-status">
          <span className="vc-my-points">
            ⭐ {my.total_points} points
          </span>
          <span className="vc-my-record">
            {my.correct_predictions} / {my.total_predictions} correct
          </span>
        </div>
      )}

      {/* Completed → reward available */}
      {comp.status === 'completed' && hasJoined && (
        <div className="vc-reward-badge">
          🏆 Check rewards to claim
        </div>
      )}

      {/* Not joined + open → CTA */}
      {!hasJoined && comp.status === 'open' && (
        <div className="vc-cta">Tap to join</div>
      )}
      {!hasJoined && comp.status === 'live' && (
        <div className="vc-cta muted">Live — join closed</div>
      )}
    </Link>
  );
};

export default VirtualCompetitions;